import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SESSION_STORAGE_VARIABLES, sessionStorageManager } from '@app/services/storageManager';
import { getUsage, postOverviewMessage, selectLocale, selectPlanInfo, selectShouldFetchUsage } from '@app/redux';
import { isVoiceOnlyPlan } from '@app/utils';
import { EventTypes } from '@app/types';

import { ModalContext, useDialogType } from '../../AddDataBoost';
import { LocalizedUsageSummary } from './localized-usage-summary';

type UsageSummaryProps = {
  navigateToPage: (link: string) => void;
  navigateToChangePlan: () => void;
};

const UsageSummary = ({ navigateToPage, navigateToChangePlan }: UsageSummaryProps) => {
  const dispatch = useDispatch();

  const {
    loading: { characteristics: characteristicsLoading, usage: usageLoading },
    data: {
      characteristics: { id },
      usage: { monthlyDataCap },
    },
  } = useSelector(selectPlanInfo);

  const shouldFetchUsage = useSelector(selectShouldFetchUsage);
  const hasNotFetchedUsage = id && !monthlyDataCap;

  const [dialogType, dialogTypeHandlers] = useDialogType();

  const {
    locale: { userLocale },
  } = useSelector(selectLocale);

  React.useEffect(() => {
    if (hasNotFetchedUsage && shouldFetchUsage) {
      dispatch(getUsage());
    }
  }, [dispatch, hasNotFetchedUsage, shouldFetchUsage]);

  const isLoading = characteristicsLoading || usageLoading;

  const handleChangePlan = React.useCallback(() => {
    navigateToChangePlan();
  }, [navigateToChangePlan]);

  const handlePlanDetailsClick = React.useCallback(() => {
    sessionStorageManager.setItem(SESSION_STORAGE_VARIABLES.SHOW_PLAN_TAB, true);
    navigateToPage('Profile');
  }, [navigateToPage]);

  const handleNavigateToPlanPage = React.useCallback(() => {
    if (window && navigateToPage) {
      navigateToPage('PlanUsage');
    }
    dispatch(postOverviewMessage({ eventType: EventTypes.VisitPlanUsage }));
  }, [dispatch, navigateToPage]);

  return (
    <ModalContext.Provider value={{ dialogType, dialogTypeHandlers }}>
      <LocalizedUsageSummary
        isLoading={isLoading}
        userLocale={userLocale}
        handleChangePlan={handleChangePlan}
        handlePlanDetailsClick={handlePlanDetailsClick}
        handleNavigateToPlanPage={handleNavigateToPlanPage}
      />
    </ModalContext.Provider>
  );
};

export default UsageSummary;
